import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  ClientApartment,
  AptReservation,
  AptReservationStatus,
  AptReservationChannel,
  AptReservationType,
  AptPaymentStatus,
  ReservationExtra,
  AptPackage,
  createAptReservation,
  updateAptReservation,
  getAptPackages,
} from "../../../utils/api/apartmentRentalsAPI";
import { ClientMenuCategory, getMenuCategories } from "../../../utils/api/menuCategoryAPI";

const { TextArea } = Input;
const { Option } = Select;

const CHANNEL_LABELS: Record<AptReservationChannel, string> = {
  direct: "Direktno",
  booking: "Booking.com",
  airbnb: "Airbnb",
  phone: "Telefon / WhatsApp",
  other: "Ostalo",
};

const STATUS_OPTIONS: { value: AptReservationStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Potvrđena" },
  { value: "staying", label: "U boravku" },
  { value: "checkout", label: "Check-out" },
  { value: "canceled", label: "Otkazana" },
  { value: "noshow", label: "No-show" },
];

const TYPE_OPTIONS: { value: AptReservationType; label: string }[] = [
  { value: "standard", label: "Standard" },
  { value: "blogger", label: "Bloger" },
  { value: "discount", label: "Akcija / Popust" },
  { value: "private", label: "Privatno" },
];

interface ReservationPrefill {
  checkIn?: string;   // YYYY-MM-DD
  checkOut?: string;  // YYYY-MM-DD
  adults?: number;
  children?: number;
  extras?: ReservationExtra[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (r: AptReservation) => void;
  apartments: ClientApartment[];
  editData?: AptReservation | null;
  clientId: string;
  defaultApartmentId?: string;
  prefill?: ReservationPrefill | null;
}

const AddReservationDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  apartments,
  editData,
  clientId,
  defaultApartmentId,
  prefill,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState<ReservationExtra[]>([]);
  const [selectedType, setSelectedType] = useState<AptReservationType>("standard");
  const [selectedChannel, setSelectedChannel] = useState<AptReservationChannel>("direct");
  const [paymentStatus, setPaymentStatus] = useState<AptPaymentStatus>("unpaid");
  const [estimatedAmount, setEstimatedAmount] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("RSD");
  const [packages, setPackages] = useState<AptPackage[]>([]);
  // Manually selected discount — admin picks explicitly, nothing auto-activates
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);
  const [selectedPromotion, setSelectedPromotion] = useState<"lastMinute" | "earlyBooking" | null>(null);
  const [menuCategories, setMenuCategories] = useState<ClientMenuCategory[]>([]);
  const [selectedMenuCategoryIds, setSelectedMenuCategoryIds] = useState<string[]>([]);

  const isEdit = !!editData;
  const checkInValue: dayjs.Dayjs | null = Form.useWatch("checkIn", form);
  const checkOutValue: dayjs.Dayjs | null = Form.useWatch("checkOut", form);
  const aptIdValue: string | undefined = Form.useWatch("apartmentId", form);
  const adultsValue: number = Form.useWatch("adults", form) ?? 2;
  const childrenValue: number = Form.useWatch("children", form) ?? 0;

  // Load packages when drawer opens
  useEffect(() => {
    if (open && clientId) {
      getAptPackages(clientId)
        .then((res) => setPackages(res.data))
        .catch(() => {});
      getMenuCategories(clientId)
        .then((res) => setMenuCategories(res.data))
        .catch(() => {});
    }
    if (!open) {
      setSelectedPkgId(null);
      setSelectedPromotion(null);
      setSelectedMenuCategoryIds([]);
    }
  }, [open, clientId]);

  // When dates/apartment change: recalc; if selected package is no longer eligible, deselect it
  useEffect(() => {
    if (!aptIdValue || !checkInValue || !checkOutValue) {
      recalcAmount(undefined, null, null);
      return;
    }
    const ci = checkInValue.format("YYYY-MM-DD");
    const co = checkOutValue.format("YYYY-MM-DD");
    const nights = checkOutValue.diff(checkInValue, "day");

    let effectivePkgId = selectedPkgId;
    if (selectedPkgId) {
      const pkg = packages.find((p) => p._id === selectedPkgId);
      if (pkg) {
        // isActive/archived deliberately excluded here: a package that's since
        // been deactivated or archived must stay attached to reservations that
        // already reference it (it's no longer offered for *new* picks, which
        // the aptPackages dropdown filter below already handles). Only an
        // actual date/apartment/nights mismatch should clear the selection.
        const eligible =
          (!pkg.apartmentId || pkg.apartmentId === aptIdValue) &&
          ci >= pkg.validFrom &&
          co <= pkg.validTo &&
          nights >= pkg.minNights;
        if (!eligible) {
          setSelectedPkgId(null);
          effectivePkgId = null;
        }
      }
    }

    recalcAmount(undefined, effectivePkgId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aptIdValue, checkInValue, checkOutValue, packages]);

  useEffect(() => {
    if (open) {
      if (editData) {
        form.setFieldsValue({
          apartmentId:
            typeof editData.apartmentId === "object"
              ? (editData.apartmentId as any)._id
              : editData.apartmentId,
          checkIn: dayjs(editData.checkIn),
          checkOut: dayjs(editData.checkOut),
          adults: editData.adults,
          children: editData.children,
          guestName: editData.guestName,
          guestEmail: editData.guestEmail,
          guestPhone: editData.guestPhone,
          instagramName: editData.instagramName,
          status: editData.status,
          amount: editData.amount,
          paidAmount: editData.paidAmount ?? (editData.isPaid ? editData.amount : 0),
          note: editData.note,
        });
        setSelectedExtras(editData.extras || []);
        setSelectedType(editData.type);
        setSelectedChannel(editData.channel);
        setPaymentStatus(editData.paymentStatus || (editData.isPaid ? "paid" : "unpaid"));
        setEstimatedAmount(editData.amount);
        setSelectedPkgId((editData as any).packageId ?? null);
        setSelectedMenuCategoryIds(editData.menuCategoryIds || []);
        const aptId =
          typeof editData.apartmentId === "object"
            ? (editData.apartmentId as any)._id
            : editData.apartmentId;
        const apt = apartments.find((a) => a._id === aptId);
        if (apt) setSelectedCurrency(apt.currency);
      } else {
        form.resetFields();
        form.setFieldsValue({
          adults: prefill?.adults ?? 2,
          children: prefill?.children ?? 0,
          status: "confirmed",
          apartmentId: defaultApartmentId,
          paidAmount: 0,
          ...(prefill?.checkIn ? { checkIn: dayjs(prefill.checkIn) } : {}),
          ...(prefill?.checkOut ? { checkOut: dayjs(prefill.checkOut) } : {}),
        });
        setSelectedExtras(prefill?.extras || []);
        setSelectedType("standard");
        setSelectedChannel("direct");
        setPaymentStatus("unpaid");
        setEstimatedAmount(0);
        setSelectedPkgId(null);
        setSelectedPromotion(null);
        const apt = apartments.find((a) => a._id === defaultApartmentId);
        setSelectedCurrency(apt?.currency ?? "RSD");
      }
    }
  }, [open, editData, defaultApartmentId, prefill, form, apartments]);

  // pkgIdOverride / promoOverride: pass explicitly when calling right after a setter (avoids stale closure)
  const recalcAmount = (
    extrasOverride?: ReservationExtra[],
    pkgIdOverride?: string | null,
    promoOverride?: "lastMinute" | "earlyBooking" | null,
  ) => {
    const aptId = form.getFieldValue("apartmentId");
    const ci: dayjs.Dayjs | null = form.getFieldValue("checkIn");
    const co: dayjs.Dayjs | null = form.getFieldValue("checkOut");
    if (!aptId || !ci || !co) return;

    const apt = apartments.find((a) => a._id === aptId);
    if (!apt) return;

    setSelectedCurrency(apt.currency);

    const nights = co.diff(ci, "day");
    if (nights <= 0) return;

    // Priority: 1) night-count rule  2) date-range rules  3) weekday rules  4) base price
    let nightsTotal = 0;
    const nightCountRule = (apt.nightCountRules || []).find((r) => r.nights === nights);
    if (nightCountRule) {
      nightsTotal = nightCountRule.pricePerNight * nights;
    } else {
      for (let n = 0; n < nights; n++) {
        const dateStr = ci.add(n, "day").format("YYYY-MM-DD");
        let price = apt.pricePerNight;
        let matched = false;
        for (const rule of apt.pricingRules || []) {
          if (dateStr >= rule.from && dateStr <= rule.to) {
            price = rule.type === "custom" ? rule.value : apt.pricePerNight * (1 - rule.value / 100);
            matched = true;
            break;
          }
        }
        if (!matched) {
          const dow = new Date(dateStr + "T12:00:00Z").getUTCDay();
          for (const rule of apt.weekdayRules || []) {
            if ((rule.days || []).includes(dow)) {
              price = rule.type === "custom" ? rule.value : apt.pricePerNight * (1 - rule.value / 100);
              break;
            }
          }
        }
        nightsTotal += price;
      }
    }

    const pkgId = pkgIdOverride !== undefined ? pkgIdOverride : selectedPkgId;
    const promo = promoOverride !== undefined ? promoOverride : selectedPromotion;

    const pkg = pkgId ? packages.find((p) => p._id === pkgId) : null;
    if (pkg) {
      const adults = form.getFieldValue("adults") ?? 2;
      const children = form.getFieldValue("children") ?? 0;
      const totalPersons = adults + children;
      const tiers = pkg.pricingByPersons || [];
      if (tiers.length > 0) {
        const sorted = [...tiers].sort((a, b) => a.persons - b.persons);
        let best = sorted[0];
        for (const t of sorted) { if (t.persons <= totalPersons) best = t; }
        nightsTotal = best.pricePerNight * nights;
      } else if (pkg.discountPercent != null) {
        nightsTotal = nightsTotal * (1 - pkg.discountPercent / 100);
      }
    } else if (promo === "lastMinute" && apt.lastMinute?.enabled) {
      nightsTotal = nightsTotal * (1 - apt.lastMinute.discount / 100);
    } else if (promo === "earlyBooking" && apt.earlyBooking?.enabled) {
      nightsTotal = nightsTotal * (1 - apt.earlyBooking.discount / 100);
    }

    const extras = extrasOverride ?? selectedExtras;
    const extrasTotal = extras.reduce((s, e) => s + e.total, 0);
    setEstimatedAmount(Math.round(nightsTotal + extrasTotal));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const paidAmt =
        paymentStatus === "paid"
          ? values.amount ?? estimatedAmount
          : paymentStatus === "partial"
          ? values.paidAmount ?? 0
          : 0;

      const payload = {
        apartmentId:
          typeof values.apartmentId === "object"
            ? (values.apartmentId as any)._id
            : values.apartmentId,
        guestName: values.guestName,
        guestEmail: values.guestEmail,
        guestPhone: values.guestPhone,
        instagramName: values.instagramName?.trim() || null,
        checkIn: (values.checkIn as dayjs.Dayjs).format("YYYY-MM-DD"),
        checkOut: (values.checkOut as dayjs.Dayjs).format("YYYY-MM-DD"),
        adults: values.adults,
        children: values.children,
        status: values.status,
        channel: selectedChannel,
        type: selectedType,
        amount: values.amount ?? estimatedAmount,
        paymentStatus,
        paidAmount: paidAmt,
        isPaid: paymentStatus === "paid",
        note: values.note,
        extras: selectedExtras,
        packageId: selectedPkgId ?? null,
        packageName: selectedPkgId ? (packages.find((p) => p._id === selectedPkgId)?.name ?? null) : null,
        menuCategoryIds: selectedMenuCategoryIds,
      };

      let result;
      if (isEdit && editData) {
        result = await updateAptReservation(editData._id, payload);
      } else {
        result = await createAptReservation(clientId, payload);
      }

      onSaved(result.data);
      onClose();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        message.error("Apartman nije slobodan za odabrane datume");
      } else if (!err?.errorFields) {
        message.error("Greška pri čuvanju rezervacije");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleExtra = (extra: ReservationExtra) => {
    setSelectedExtras((prev) => {
      const exists = prev.find((e) => e.extraId === extra.extraId);
      const next = exists
        ? prev.filter((e) => e.extraId !== extra.extraId)
        : [...prev, extra];
      recalcAmount(next);
      return next;
    });
  };

  const updateExtraQty = (extraId: string, qty: number) => {
    setSelectedExtras((prev) => {
      const next = prev.map((e) =>
        e.extraId === extraId ? { ...e, quantity: qty, total: e.pricePerUnit * qty } : e,
      );
      recalcAmount(next);
      return next;
    });
  };

  const fmtAmount = (n: number, currency?: string) =>
    new Intl.NumberFormat("sr-RS").format(n) + " " + (currency ?? selectedCurrency);

  // Compute which packages apply to the selected apartment and whether they're eligible
  const aptPackages = (() => {
    if (!aptIdValue) return [];
    return packages.filter(
      (p) => p.isActive && (!p.apartmentId || p.apartmentId === aptIdValue),
    );
  })();

  const isPkgEligible = (pkg: AptPackage): boolean => {
    if (!checkInValue || !checkOutValue) return false;
    const ci = checkInValue.format("YYYY-MM-DD");
    const co = checkOutValue.format("YYYY-MM-DD");
    const nights = checkOutValue.diff(checkInValue, "day");
    return ci >= pkg.validFrom && co <= pkg.validTo && nights >= pkg.minNights;
  };

  const exportSingleToExcel = useCallback(() => {
    if (!editData) return;
    const apt = apartments.find((a) => {
      const id = typeof editData.apartmentId === "object" ? (editData.apartmentId as any)._id : editData.apartmentId;
      return a._id === id;
    });
    const aptName = typeof editData.apartmentId === "object" ? (editData.apartmentId as any).name : apt?.name ?? "";
    const nights = Math.max(0, dayjs(editData.checkOut).diff(dayjs(editData.checkIn), "day"));
    const CHANNEL_LABEL: Record<string, string> = {
      direct: "Direktno", booking: "Booking", airbnb: "Airbnb", phone: "Telefon", other: "Ostalo",
    };
    const TYPE_LABEL: Record<string, string> = {
      standard: "Standardna", blogger: "Blogger", discount: "Popust", private: "Privatno",
    };
    const STATUS_LABEL: Record<string, string> = {
      confirmed: "Potvrđena", pending: "Pending", staying: "U boravku",
      checkout: "Check-out danas", canceled: "Otkazana", noshow: "No-show",
    };

    const rows = [
      {
        "Ime gosta":       editData.guestName,
        "Email":           editData.guestEmail ?? "",
        "Telefon":         editData.guestPhone ?? "",
        "Instagram":       editData.instagramName ?? "",
        "Apartman":        aptName,
        "Check-in":        dayjs(editData.checkIn).format("DD.MM.YYYY"),
        "Check-out":       dayjs(editData.checkOut).format("DD.MM.YYYY"),
        "Broj noći":       nights,
        "Odrasli":         editData.adults,
        "Djeca":           editData.children ?? 0,
        "Kanal":           CHANNEL_LABEL[editData.channel] ?? editData.channel,
        "Status":          STATUS_LABEL[editData.status] ?? editData.status,
        "Tip":             TYPE_LABEL[editData.type] ?? editData.type,
        "Iznos":           editData.amount,
        "Valuta":          apt?.currency ?? "RSD",
        "Plaćeno":         editData.paidAmount ?? 0,
        "Status plaćanja": editData.paymentStatus === "paid" ? "Plaćeno" : editData.paymentStatus === "partial" ? "Djelimično" : "Nije plaćeno",
        "Paket":           editData.packageName ?? "",
        "Promo kod":       editData.promoCode ?? "",
        "Popust":          editData.promoDiscount ?? 0,
        "Napomena":        editData.note ?? "",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.max(key.length, String((rows[0] as any)[key] ?? "").length) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rezervacija");
    const slug = editData.guestName.replace(/\s+/g, "-").toLowerCase();
    XLSX.writeFile(wb, `rezervacija-${slug}.xlsx`);
  }, [editData, apartments]);

  const selectedApt = apartments.find((a) => a._id === aptIdValue);
  // A reservation can carry a packageId that's no longer offered (archived,
  // deactivated, or its rules changed) — still show the section so that
  // attachment stays visible instead of silently disappearing.
  const attachedPkgNotListed = !!selectedPkgId && !aptPackages.some((p) => p._id === selectedPkgId);
  const hasDiscountSection =
    aptPackages.length > 0 ||
    selectedApt?.lastMinute?.enabled ||
    selectedApt?.earlyBooking?.enabled ||
    attachedPkgNotListed;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "#ede9fd",
              color: "#5b50d6",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            📋
          </span>
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#5b50d6",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {isEdit ? "Izmijeni rezervaciju" : "Nova rezervacija"}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
              {isEdit ? "Uredi detalje boravka" : "Dodaj boravak"}
              {isEdit && editData && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#5b50d6",
                    background: "#ede9fd",
                    borderRadius: 6,
                    padding: "2px 8px",
                  }}
                >
                  {Math.max(1, dayjs(editData.checkOut).diff(dayjs(editData.checkIn), "day"))} noćenja
                </span>
              )}
            </div>
          </div>
        </div>
      }
      width={500}
      destroyOnClose
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
      footer={
        <div className="ar-drawer-footer-inner">
          <div className="ar-drawer-footer-amount">
            <div className="ar-drawer-footer-amount__label">Procijenjeni iznos</div>
            <div className="ar-drawer-footer-amount__val">{fmtAmount(estimatedAmount)}</div>
          </div>
          <div className="ar-drawer-footer-actions">
            {isEdit && (
              <Button onClick={exportSingleToExcel}>Excel</Button>
            )}
            <Button onClick={onClose}>Otkaži</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleSave}
              style={{ background: "#1a1520", borderColor: "#1a1520" }}
            >
              {isEdit ? "Sačuvaj izmjene" : "Sačuvaj rezervaciju"}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
        <Form form={form} layout="vertical" onValuesChange={() => recalcAmount()}>
          {/* Tip rezervacije */}
          <div className="ar-drawer-section">
            <div className="ar-drawer-section-title">Tip rezervacije</div>
            <div className="ar-seg-pick">
              {TYPE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={selectedType === t.value ? "active" : ""}
                  onClick={() => setSelectedType(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Apartman i datumi */}
          <div className="ar-drawer-section">
            <div className="ar-drawer-section-title">Apartman i datumi</div>

            <Form.Item
              name="apartmentId"
              label="Apartman"
              rules={[{ required: true, message: "Odaberite apartman" }]}
              style={{ marginBottom: 12 }}
            >
              <Select placeholder="Odaberite apartman">
                {apartments.map((a) => (
                  <Option key={a._id} value={a._id}>
                    {a.name} · {a.capacity} gosta · {fmtAmount(a.pricePerNight, a.currency)}/noć
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Row gutter={10}>
              <Col span={12}>
                <Form.Item
                  name="checkIn"
                  label="Check-in"
                  rules={[{ required: true, message: "Odaberite datum" }]}
                  style={{ marginBottom: 12 }}
                >
                  <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="checkOut"
                  label="Check-out"
                  rules={[{ required: true, message: "Odaberite datum" }]}
                  style={{ marginBottom: 12 }}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD.MM.YYYY"
                    disabledDate={(d) => !!checkInValue && d.isBefore(checkInValue.add(1, "day"), "day")}
                    {...(checkInValue ? { defaultPickerValue: checkInValue.add(1, "day") } : {})}
                  />
                </Form.Item>
              </Col>
            </Row>

            {checkInValue && checkOutValue && checkOutValue.diff(checkInValue, "day") > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#5b50d6",
                  background: "#ede9fd",
                  borderRadius: 7,
                  padding: "4px 10px",
                  marginBottom: 12,
                }}
              >
                🌙 {checkOutValue.diff(checkInValue, "day")} {checkOutValue.diff(checkInValue, "day") === 1 ? "noćenje" : "noćenja"}
              </div>
            )}

            <Row gutter={10}>
              <Col span={12}>
                <Form.Item name="adults" label="Odrasli" style={{ marginBottom: 12 }}>
                  <InputNumber min={1} max={20} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="children" label="Djeca" style={{ marginBottom: 12 }}>
                  <InputNumber min={0} max={10} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Akcije i promocije — all options shown, admin picks manually */}
          {hasDiscountSection && (
            <div className="ar-drawer-section">
              <div className="ar-drawer-section-title">Akcije i promocije</div>
              {(!checkInValue || !checkOutValue) && (
                <div style={{ fontSize: 11, color: "#9893a8", marginBottom: 8 }}>
                  Odaberite datume da vidite koje akcije možete primijeniti
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {/* Packages */}
                {aptPackages.map((pkg) => {
                  const eligible = isPkgEligible(pkg);
                  const isSelected = selectedPkgId === pkg._id;
                  const totalPersons = adultsValue + childrenValue;
                  const tiers = pkg.pricingByPersons || [];
                  let discountLabel = "";
                  if (tiers.length > 0) {
                    const sorted = [...tiers].sort((a, b) => a.persons - b.persons);
                    let best = sorted[0];
                    for (const t of sorted) { if (t.persons <= totalPersons) best = t; }
                    discountLabel = `${best.pricePerNight} ${pkg.currency ?? selectedCurrency}/noć`;
                  } else if (pkg.discountPercent != null) {
                    discountLabel = `−${pkg.discountPercent}%`;
                  } else if (pkg.pricePerNight != null) {
                    discountLabel = `${pkg.pricePerNight} ${pkg.currency ?? selectedCurrency}/noć`;
                  }

                  let eligibilityNote = "";
                  if (!checkInValue || !checkOutValue) {
                    eligibilityNote = "odaberite datume";
                  } else if (!eligible) {
                    const nights = checkOutValue.diff(checkInValue, "day");
                    if (nights < pkg.minNights) {
                      eligibilityNote = `min ${pkg.minNights} noći`;
                    } else {
                      eligibilityNote = `van perioda važenja`;
                    }
                  }

                  return (
                    <button
                      key={pkg._id}
                      type="button"
                      disabled={!eligible}
                      onClick={() => {
                        const newId = isSelected ? null : pkg._id;
                        setSelectedPkgId(newId);
                        setSelectedPromotion(null);
                        recalcAmount(undefined, newId, null);
                      }}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1.5px solid ${isSelected ? "#5b50d6" : eligible ? "#d1cef7" : "#e8e5f0"}`,
                        background: isSelected ? "#f3f0ff" : eligible ? "#faf9ff" : "#fafafa",
                        cursor: eligible ? "pointer" : "not-allowed",
                        opacity: eligible ? 1 : 0.55,
                        width: "100%",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 14 }}>🎁</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#5b50d6" : "#1a1520" }}>
                            {pkg.name}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {discountLabel && (
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: "2px 7px",
                              borderRadius: 20,
                              background: isSelected ? "#5b50d6" : eligible ? "#ede9fd" : "#f0eeff",
                              color: isSelected ? "#fff" : "#5b50d6",
                            }}>
                              {discountLabel}
                            </span>
                          )}
                          {!eligible && eligibilityNote && (
                            <span style={{ fontSize: 10, color: "#9893a8", fontWeight: 600 }}>
                              {eligibilityNote}
                            </span>
                          )}
                          {eligible && isSelected && (
                            <span style={{ fontSize: 13, color: "#5b50d6", fontWeight: 800 }}>✓</span>
                          )}
                        </div>
                      </div>
                      {pkg.description && (
                        <div style={{ fontSize: 11, color: "#7c6880", marginTop: 4, marginLeft: 20 }}>
                          {pkg.description}
                        </div>
                      )}
                      {eligible && pkg.includes?.filter((i) => i.label).length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 6, marginLeft: 20 }}>
                          {pkg.includes.filter((i) => i.label).map((inc, idx) => (
                            <span key={idx} style={{
                              fontSize: 10, fontWeight: 600, padding: "1px 6px",
                              borderRadius: 20, background: "rgba(91,80,214,0.1)", color: "#5b50d6",
                            }}>
                              ✓ {inc.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}

                {/* Currently attached package that's no longer offered (e.g. it
                    was archived since this reservation was made) — shown
                    read-only so the historical attachment stays visible and
                    isn't silently dropped when the reservation is saved. */}
                {attachedPkgNotListed && (() => {
                  const attachedPkg = packages.find((p) => p._id === selectedPkgId);
                  return (
                    <div
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                        padding: "10px 12px", borderRadius: 8,
                        border: "1.5px dashed #d8d4e8", background: "#fafafa",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14 }}>🎁</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#6b6880" }}>
                          {attachedPkg?.name || editData?.packageName || "Akcija"}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                          background: "#eee", color: "#9893a8",
                        }}>
                          nije više dostupno
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPkgId(null);
                          recalcAmount(undefined, null, null);
                        }}
                        style={{
                          border: "none", background: "transparent", color: "#9893a8",
                          cursor: "pointer", fontSize: 12, fontWeight: 600,
                        }}
                      >
                        Ukloni
                      </button>
                    </div>
                  );
                })()}

                {/* Last Minute promotion */}
                {selectedApt?.lastMinute?.enabled && (() => {
                  const lm = selectedApt.lastMinute!;
                  const isSelected = selectedPromotion === "lastMinute";
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        const newVal = isSelected ? null : "lastMinute" as const;
                        setSelectedPromotion(newVal);
                        setSelectedPkgId(null);
                        recalcAmount(undefined, null, newVal);
                      }}
                      style={{
                        textAlign: "left", padding: "10px 12px", borderRadius: 8,
                        border: `1.5px solid ${isSelected ? "#d97706" : "#fde68a"}`,
                        background: isSelected ? "#fffbeb" : "#fffdf5",
                        cursor: "pointer", width: "100%",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 14 }}>⚡</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#b45309" : "#1a1520" }}>
                            Last Minute
                            {lm.label && <span style={{ fontWeight: 400, marginLeft: 6, color: "#9893a8" }}>{lm.label}</span>}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                            background: isSelected ? "#d97706" : "#fef3c7", color: isSelected ? "#fff" : "#b45309",
                          }}>
                            −{lm.discount}%
                          </span>
                          {isSelected && <span style={{ fontSize: 13, color: "#d97706", fontWeight: 800 }}>✓</span>}
                        </div>
                      </div>
                    </button>
                  );
                })()}

                {/* Early Booking promotion */}
                {selectedApt?.earlyBooking?.enabled && (() => {
                  const eb = selectedApt.earlyBooking!;
                  const isSelected = selectedPromotion === "earlyBooking";
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        const newVal = isSelected ? null : "earlyBooking" as const;
                        setSelectedPromotion(newVal);
                        setSelectedPkgId(null);
                        recalcAmount(undefined, null, newVal);
                      }}
                      style={{
                        textAlign: "left", padding: "10px 12px", borderRadius: 8,
                        border: `1.5px solid ${isSelected ? "#0284c7" : "#bae6fd"}`,
                        background: isSelected ? "#f0f9ff" : "#f9fdff",
                        cursor: "pointer", width: "100%",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 14 }}>🗓</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#0369a1" : "#1a1520" }}>
                            Early Booking
                            {eb.label && <span style={{ fontWeight: 400, marginLeft: 6, color: "#9893a8" }}>{eb.label}</span>}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                            background: isSelected ? "#0284c7" : "#e0f2fe", color: isSelected ? "#fff" : "#0369a1",
                          }}>
                            −{eb.discount}%
                          </span>
                          {isSelected && <span style={{ fontSize: 13, color: "#0284c7", fontWeight: 800 }}>✓</span>}
                        </div>
                      </div>
                    </button>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Gost */}
          <div className="ar-drawer-section">
            <div className="ar-drawer-section-title">Gost</div>

            <Form.Item
              name="guestName"
              label="Ime i prezime"
              rules={[{ required: true, message: "Unesite ime gosta" }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="npr. Marija Kostić" />
            </Form.Item>

            <Row gutter={10}>
              <Col span={12}>
                <Form.Item name="guestEmail" label="Email" style={{ marginBottom: 12 }}>
                  <Input type="email" placeholder="ime@mail.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="guestPhone" label="Telefon" style={{ marginBottom: 12 }}>
                  <Input placeholder="+381 ..." />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="instagramName" label="Instagram" style={{ marginBottom: 12 }}>
              <Input prefix="@" placeholder="korisnicko.ime" />
            </Form.Item>

            <Form.Item label="Kanal" style={{ marginBottom: 12 }}>
              <div className="ar-seg-pick">
                {(Object.keys(CHANNEL_LABELS) as AptReservationChannel[]).map((ch) => (
                  <button
                    key={ch}
                    type="button"
                    className={selectedChannel === ch ? "active" : ""}
                    onClick={() => setSelectedChannel(ch)}
                  >
                    {CHANNEL_LABELS[ch]}
                  </button>
                ))}
              </div>
            </Form.Item>
          </div>

          {/* Status i iznos */}
          <div className="ar-drawer-section">
            <div className="ar-drawer-section-title">Status i plaćanje</div>

            <Form.Item name="status" label="Status rezervacije" style={{ marginBottom: 12 }}>
              <Select>
                {STATUS_OPTIONS.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="amount" label="Ukupni iznos" style={{ marginBottom: 12 }}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={`Procjena: ${estimatedAmount.toLocaleString("sr-RS")}`}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(v) => Number(v?.replace(/\./g, "")) as any}
                onChange={(v) => setEstimatedAmount(v || 0)}
              />
            </Form.Item>

            {/* Payment status picker */}
            <Form.Item
              label="Status plaćanja"
              style={{ marginBottom: paymentStatus === "partial" ? 12 : 0 }}
            >
              <div className="ar-payment-pick">
                {(
                  [
                    { value: "unpaid", label: "Nije plaćeno", icon: "○" },
                    { value: "partial", label: "Avans plaćen", icon: "◑" },
                    { value: "paid", label: "Plaćeno u cijelosti", icon: "●" },
                  ] as { value: AptPaymentStatus; label: string; icon: string }[]
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`ar-payment-opt ar-payment-opt--${opt.value}${paymentStatus === opt.value ? " active" : ""}`}
                    onClick={() => {
                      setPaymentStatus(opt.value);
                      if (opt.value === "paid") {
                        form.setFieldValue("paidAmount", form.getFieldValue("amount") ?? estimatedAmount);
                      } else if (opt.value === "unpaid") {
                        form.setFieldValue("paidAmount", 0);
                      }
                    }}
                  >
                    <span className="ic">{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </Form.Item>

            {paymentStatus === "partial" && (
              <>
                <Form.Item
                  name="paidAmount"
                  label="Avans / uplaćeni iznos"
                  style={{ marginBottom: 6 }}
                >
                  <InputNumber
                    min={0}
                    max={form.getFieldValue("amount") ?? estimatedAmount}
                    style={{ width: "100%" }}
                    placeholder="0"
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                    parser={(v) => Number(v?.replace(/\./g, "")) as any}
                  />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {() => {
                    const total = form.getFieldValue("amount") ?? estimatedAmount;
                    const paid = form.getFieldValue("paidAmount") ?? 0;
                    const remaining = Math.max(0, total - paid);
                    return (
                      <div className="ar-payment-remaining">
                        <span>Ostatak za naplatu</span>
                        <strong>{remaining.toLocaleString("sr-RS")}</strong>
                      </div>
                    );
                  }}
                </Form.Item>
              </>
            )}
          </div>

          {/* Dodatne usluge */}
          {(() => {
            const aptId = form.getFieldValue("apartmentId");
            const apt = apartments.find((a) => a._id === aptId);
            const aptExtras = apt?.extras || [];
            if (aptExtras.length === 0) return null;
            return (
              <div className="ar-drawer-section">
                <div className="ar-drawer-section-title">Dodatne usluge</div>
                {aptExtras.map((extra) => {
                  const eid = extra._id || extra.name;
                  const sel = selectedExtras.find((e) => e.extraId === eid);
                  const activePrice = extra.discountedPrice != null ? extra.discountedPrice : extra.regularPrice;
                  return (
                    <div
                      key={eid}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 10px", marginBottom: 6, borderRadius: 8,
                        border: `1.5px solid ${sel ? "#5b50d6" : "#e8e5f0"}`,
                        background: sel ? "#f3f0ff" : "#fafafa", cursor: "pointer",
                      }}
                      onClick={() =>
                        toggleExtra({
                          extraId: eid, name: extra.name,
                          quantity: 1, pricePerUnit: activePrice, total: activePrice,
                        })
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!!sel}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: "#5b50d6", flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1520" }}>{extra.name}</div>
                        <div style={{ fontSize: 11, color: "#6b6880" }}>
                          {extra.discountedPrice != null && (
                            <span style={{ textDecoration: "line-through", marginRight: 4, color: "#9893a8" }}>
                              {fmtAmount(extra.regularPrice)}
                            </span>
                          )}
                          <span style={{ color: extra.discountedPrice != null ? "#16a34a" : "#1a1520", fontWeight: 600 }}>
                            {fmtAmount(activePrice)}
                          </span>
                          {extra.unit && <span style={{ marginLeft: 3 }}>{extra.unit}</span>}
                        </div>
                      </div>
                      {sel && (
                        <div
                          style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => updateExtraQty(eid!, Math.max(1, sel.quantity - 1))}
                            style={{ width: 22, height: 22, borderRadius: 5, border: "1px solid #c4bfef", background: "#fff", cursor: "pointer", fontWeight: 700, color: "#5b50d6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}
                          >−</button>
                          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 16, textAlign: "center" }}>{sel.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateExtraQty(eid!, sel.quantity + 1)}
                            style={{ width: 22, height: 22, borderRadius: 5, border: "1px solid #c4bfef", background: "#fff", cursor: "pointer", fontWeight: 700, color: "#5b50d6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}
                          >+</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Obroci — izbor gosta sa javne rezervacije (read-only; bira se na booking stranici) */}
          {(() => {
            const sels = editData?.mealSelections;
            if (!sels || sels.length === 0) return null;
            const byDate = new Map<string, typeof sels>();
            for (const ms of sels) {
              const list = byDate.get(ms.date) ?? [];
              list.push(ms);
              byDate.set(ms.date, list);
            }
            const dates = Array.from(byDate.keys()).sort();
            return (
              <div className="ar-drawer-section">
                <div className="ar-drawer-section-title">Obroci — izbor gosta</div>
                {dates.map((date) => (
                  <div key={date} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "#6b6880", marginBottom: 4, textTransform: "capitalize" }}>
                      {dayjs(date).format("dddd, DD.MM.YYYY")}
                    </div>
                    {byDate.get(date)!.map((ms, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                          padding: "6px 10px", marginBottom: 4, borderRadius: 6,
                          background: "#fafafa", border: "1px solid #e8e5f0", fontSize: 12.5,
                        }}
                      >
                        <span>
                          <strong>{ms.mealName}</strong>{ms.itemName ? ` — ${ms.itemName}` : ""} ·{" "}
                          {typeof ms.persons === "number" && ms.persons > 0
                            ? `${ms.persons} ${ms.persons === 1 ? "osoba" : "osobe/a"}`
                            : "broj osoba nepoznat (rezervacija prije uvođenja ovog polja)"}
                        </span>
                        <span style={{ fontWeight: 700, color: ms.isIncluded ? "#16a34a" : "#1a1520", flexShrink: 0 }}>
                          {ms.isIncluded ? "uključeno" : fmtAmount(ms.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
                {editData?.mealsTotal != null && editData.mealsTotal > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, paddingTop: 6, borderTop: "1px solid #e8e5f0" }}>
                    <span>Ukupno obroci</span>
                    <span>{fmtAmount(editData.mealsTotal)}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Meni za goste (QR kod) — opciono */}
          {(() => {
            const apt = apartments.find((a) => a._id === aptIdValue);
            const linkedIds = new Set(
              (apt?.menuCategories || []).filter((mc) => mc.isActive).map((mc) => mc.categoryId)
            );
            const availableCategories = menuCategories.filter((c) => c.isActive && linkedIds.has(c._id));
            if (availableCategories.length === 0) return null;

            const toggleMenuCategory = (id: string) =>
              setSelectedMenuCategoryIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              );

            return (
              <div className="ar-drawer-section">
                <div className="ar-drawer-section-title">Meni za goste (QR kod) — opciono</div>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 8 }}>
                  Važi tačno za boravak ovog gosta (od 14:00 na check-in do 11:00 na check-out). Ostavi prazno da važi podrazumijevani meni apartmana.
                </div>
                {availableCategories.map((cat) => {
                  const sel = selectedMenuCategoryIds.includes(cat._id);
                  return (
                    <div
                      key={cat._id}
                      onClick={() => toggleMenuCategory(cat._id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 10px", marginBottom: 6, borderRadius: 8,
                        border: `1.5px solid ${sel ? "#5b50d6" : "#e8e5f0"}`,
                        background: sel ? "#f3f0ff" : "#fafafa", cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: "#5b50d6", flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 15 }}>{cat.emoji}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1520" }}>{cat.name}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Promo kod — read-only info when editing */}
          {editData?.promoCode && (
            <div className="ar-drawer-section">
              <div className="ar-drawer-section-title">Promo kod</div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", background: "#f0fdf4", border: "1px solid #86efac",
                borderRadius: 10, fontSize: 13,
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#15803d", letterSpacing: "0.05em" }}>
                    🎉 {editData.promoCode}
                  </span>
                  {editData.promoDiscount ? (
                    <span style={{ color: "#16a34a", fontSize: 12 }}>
                      Popust: −{new Intl.NumberFormat("sr-RS").format(editData.promoDiscount)}{" "}
                      {editData.baseAmount
                        ? `(osnova: ${new Intl.NumberFormat("sr-RS").format(editData.baseAmount)})`
                        : ""}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Bilješka */}
          <div className="ar-drawer-section">
            <div className="ar-drawer-section-title">Bilješka</div>
            <Form.Item name="note" style={{ marginBottom: 0 }}>
              <TextArea
                rows={3}
                placeholder="Posebne napomene (alergije, preferencije, dolazi sa kućnim ljubimcem...)"
              />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Drawer>
  );
};

export default AddReservationDrawer;
